import { DocumentImageCache } from './document-image-cache';
import { AuthSessionCoordinatorService } from 'src/app/login/auth-session-coordinator.service';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Subject, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PRESERVE_HTTP_ERROR } from 'src/app/shared/utils/preserve-http-error.context';
import type { ImportDoc } from 'src/typings';
import {
  DocumentHistory,
  DocumentLink,
  DocumentLinkKind,
  DocumentTargetFilters,
  DocumentPage,
  DocumentReview,
  DocumentScanner,
  DocumentSession,
  DocumentSessionSummary,
  DiscoveredScanner,
  ScannerInput,
  ScanJob
} from './document-receiving.types';

@Injectable({ providedIn: 'root' })
export class DocumentReceivingService {
  private readonly url = `${environment.BACKEND}/document-receiving`;
  private readonly queueChanges = new Subject<void>();
  readonly queueChanged$ = this.queueChanges.asObservable();
  notifyQueueChanged() { this.queueChanges.next(); }
  private readonly imageCache = new DocumentImageCache();
  private imageSession: string | null | undefined;
  constructor(private http: HttpClient,
    @Optional() private authSession: AuthSessionCoordinatorService | null = null) {}

  private options(params: { [key: string]: string | number | undefined } = {}) {
    let query = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') query = query.set(key, value);
    });
    return { params: query, context: new HttpContext().set(PRESERVE_HTTP_ERROR, true) };
  }
  sessions(params: {
    warehouseId?: number;
    queue: string;
    unlinkedOnly?: boolean;
    offset?: number;
    limit?: number;
    targetType?: string;
    targetId?: number;
  }) {
    return this.http.get<{ items: DocumentSessionSummary[]; total: number }>(
      `${this.url}/sessions`,
      this.options({ ...params, unlinkedOnly: params.unlinkedOnly === undefined ? undefined : String(params.unlinkedOnly) })
    );
  }
  reviewCount() {
    return this.http.get<{ count: number }>(`${this.url}/review-count`, this.options());
  }
  start(warehouseId: number, operationId: string) {
    return this.http.post<DocumentSession>(
      `${this.url}/sessions/start-or-resume`,
      { warehouseId, operationId },
      this.options()
    );
  }
  session(id: number) {
    return this.http.get<DocumentSession>(`${this.url}/sessions/${id}`, this.options());
  }
  history(id: number) {
    return this.http.get<DocumentHistory[]>(`${this.url}/sessions/${id}/history`, this.options());
  }
  finish(id: number, expectedRevision: number, operationId: string) {
    return this.http.post<DocumentSession>(
      `${this.url}/sessions/${id}/finish`,
      { expectedRevision, operationId },
      this.options()
    ).pipe(tap(() => this.queueChanges.next()));
  }
  upload(session: DocumentSession, file: File, operationId: string, replacesPageId?: number) {
    const body = new FormData();
    body.append('file', file);
    body.append('operationId', operationId);
    body.append('expectedRevision', String(session.revision));
    if (replacesPageId) body.append('replacesPageId', String(replacesPageId));
    return this.http.post<DocumentSession>(
      `${this.url}/sessions/${session.id}/files`,
      body,
      this.options()
    );
  }
  retryUpload(jobId: string, operationId: string) {
    return this.http.post<ScanJob>(`${this.url}/jobs/${jobId}/retry-upload`, { operationId }, this.options());
  }
  closeEmpty(id: number, expectedRevision: number, operationId: string) {
    return this.http.post<DocumentSession>(`${this.url}/sessions/${id}/close-empty`,
      { expectedRevision, operationId }, this.options());
  }
  changePage(session: DocumentSession, page: DocumentPage, action: 'remove' | 'restore', operationId: string) {
    return this.http.post<DocumentSession>(`${this.url}/sessions/${session.id}/pages/${page.id}/${action}`,
      { expectedRevision: session.revision, expectedVersion: page.version, operationId }, this.options());
  }
  image(id: number, version = 0) {
    const session = this.authSession?.getCurrentFingerprint();
    if (session !== this.imageSession) {
      this.imageCache.clear();
      this.imageSession = session;
    }
    return this.imageCache.get(`${id}:${version}`, () => this.http.get(`${this.url}/pages/${id}/image`, {
      ...this.options(),
      responseType: 'blob'
    }));
  }
  original(id: number) {
    return this.http.get(`${this.url}/pages/${id}/original`, {
      ...this.options(),
      responseType: 'blob'
    });
  }
  scanners(warehouseId: number) {
    return this.http.get<DocumentScanner[]>(`${this.url}/scanners`, this.options({ warehouseId }));
  }
  scan(
    id: number,
    body: {
      operationId: string;
      scannerId: number;
      mode: 'Grayscale' | 'Color';
      source?: 'Flatbed' | 'Feeder' | 'Duplex';
      replacesPageId?: number;
    }
  ) {
    return this.http.post<ScanJob>(`${this.url}/sessions/${id}/scan`, body, this.options());
  }
  job(id: string) {
    return this.http.get<ScanJob>(`${this.url}/jobs/${id}`, this.options());
  }
  operation(id: string) {
    return this.http.get<{ job: ScanJob | null }>(`${this.url}/operations/${id}`, this.options());
  }
  cancel(id: string, operationId: string) {
    return this.http.post<ScanJob>(
      `${this.url}/jobs/${id}/cancel`,
      { operationId },
      this.options()
    );
  }
  targets(kind: DocumentLinkKind, q: string, warehouseId?: number, filters: DocumentTargetFilters = {}) {
    return this.http.get<DocumentLink[]>(
      `${this.url}/targets`,
      this.options({ ...filters, kind, q, warehouseId })
    );
  }
  related(importIds: number[]) {
    return this.http.get<DocumentLink[]>(
      `${this.url}/related`,
      this.options({ importIds: importIds.join(',') })
    );
  }
  importContents(id: number) {
    return this.http.get<ImportDoc | null>(`${environment.BACKEND}/doc/import/${id}`, this.options());
  }
  linkedPages(kind: DocumentLinkKind, targetId: number) {
    return this.http.get<DocumentPage[]>(
      `${this.url}/linked-pages`,
      this.options({ kind, targetId })
    );
  }
  review(id: number, body: DocumentReview) {
    return this.http.post<DocumentSession>(
      `${this.url}/sessions/${id}/review`,
      body,
      this.options()
    ).pipe(tap(() => this.queueChanges.next()));
  }
  registry() {
    return this.http.get<DocumentScanner[]>(`${this.url}/scanner-registry`, this.options());
  }
  discovery() {
    return this.http.get<DiscoveredScanner[]>(`${this.url}/scanner-discovery`, this.options());
  }
  saveScanner(body: ScannerInput, id?: number) {
    return id
      ? this.http.put<DocumentScanner>(`${this.url}/scanner-registry/${id}`, body, this.options())
      : this.http.post<DocumentScanner>(`${this.url}/scanner-registry`, body, this.options());
  }
}
